import { Net, createNetNode } from "@buildwithflux/models";
import { keySort } from "@buildwithflux/shared";
import { cloneDeep, difference } from "lodash";
import { addNet, getNetHashMap, removePinsFromNet } from "../document/models";
import { addPcbNodes } from "../nodes";
import { getNetName } from "./getNetName";
// TODO FLUX-5090 it's not very clean to have this function delete/create/update nets and nodes piecemeal. It'd
// be far better to do all the deletions/syncing in one place (in updateNetNodes() where we do it
// anyway) and ensure all nets and nodes have the right pins, and *then* merge only the properties and
// rules. Just make sure we still preserve net uids of winner nets properly.
// Same for mergeNets().
export function splitNets(document, split, pcbNodes, netHashLookup, netDesignatorSequencer, allNewNets, netsContainerUid) {
    const splitNetHashes = [];
    const oldNetsHashMap = getNetHashMap(document.nets);
    const newSplitNodes = Object.entries(split).flatMap(([oldNetHash, newNetHashes]) => {
        // Sort ground nets to the front of the list
        // TODO FLUX-5090: these are already sorted by num pins (complexity) when generated.
        // Why not also do this sort at the same time?
        const [splitWinner, ...splitLosers] = newNetHashes.sort(keySort((uid) => (allNewNets[uid].isGroundNet() ? 0 : 1)));
        if (!splitWinner) {
            throw new Error("No split winner");
        }
        // The split winner keeps the ID and name of the old net
        // We just remove the pins and children that are now in the split losers
        // TODO FLUX-5090 incredibly confusing naming here. `splitWinnerNet` is the old net,
        // which we'll be preserving-and-updating.
        const splitWinnerNet = document.nets[netHashLookup[oldNetHash]];
        if (!splitWinnerNet) {
            throw new Error("No split winner net");
        }
        const newNodes = [];
        // Try to get the existing net node. In some cases the net node may have been deleted
        // by the user so we need to create a new one
        let splitWinnerNode = pcbNodes[splitWinnerNet.uid];
        if (!splitWinnerNode) {
            splitWinnerNode = createNetNode(splitWinnerNet.uid, netsContainerUid, splitWinnerNet.label, {
                // NOTE: these are filled in below
                topLevel: [],
                submodule: [],
            });
            newNodes.push(splitWinnerNode);
        }
        splitNetHashes.push(oldNetHash);
        // Update terminals on split winner
        const terminals = allNewNets[splitWinner]?.terminalsByDocLevel();
        if (terminals) {
            // Remove any pins that are no longer in the winner net
            // TODO FLUX-5090 I don't believe we can get away without also updating any
            // added pins! Especially now that we have series overlap, it's
            // easy to split a net at the same time as adding pins to it. This
            // is a serious hole.
            removePinsFromNet(document.nets, splitWinnerNet.uid, difference(Object.keys(splitWinnerNet.pins), terminals.topLevel));
            // Update the node pins
            splitWinnerNode.terminals = Net.terminalsToMap(terminals.topLevel);
            splitWinnerNode.submoduleTerminals = Net.terminalsToMap(terminals.submodule);
        }
        // Check if the old net was a ground net, but the new net is not
        const oldNetIsGround = Net.terminalsIncludeGround(Object.keys(oldNetsHashMap[oldNetHash]), document.elements);
        const newNetIsGround = allNewNets[splitWinner]?.isGroundNet();
        if (oldNetIsGround && !newNetIsGround) {
            // Create a new name for the net that is no longer grounded
            const name = getNetName(Net.terminalsToMap(terminals?.topLevel ?? []), document.elements, netDesignatorSequencer);
            splitWinnerNode.name = name;
            splitWinnerNet.label = name;
        }
        // Update split winner hash
        splitWinnerNet.hash = splitWinner;
        const splitWinnerUserData = cloneDeep(splitWinnerNode.userCodeData);
        const splitWinnerRules = cloneDeep(splitWinnerNode.pcbNodeRuleSet);
        const splitLoserNodes = splitLosers.map((splitLoser) => {
            const terminals = allNewNets[splitLoser]?.terminalsByDocLevel();
            splitNetHashes.push(splitLoser);
            // Create a new document net for the split loser
            const net = addNet(document.nets, splitLoser, getNetName(Net.terminalsToMap(terminals?.topLevel ?? []), document.elements, netDesignatorSequencer), terminals?.topLevel ?? [], cloneDeep(splitWinnerNet.properties));
            // Create a new net node for the split loser
            const netNode = createNetNode(net.uid, splitWinnerNode.parentUid, net.label, terminals);
            netNode.userCodeData = splitWinnerUserData;
            netNode.pcbNodeRuleSet = splitWinnerRules;
            return netNode;
        });
        newNodes.push(...splitLoserNodes);
        // Add the new nodes to the document
        addPcbNodes(pcbNodes, newNodes);
        return newNodes;
    });
    return { newSplitNodes, splitNetHashes };
}
//# sourceMappingURL=splitNets.js.map